// Every saved change to the event, newest first (Lane 1322). One row per
// action receipt, whether it came from the screen or from chat.
import React, { useCallback, useEffect, useState } from 'react';
import { TableShell } from '../../components/ui/DataPrimitives';

export const ACTION_TEXT = {
  create_event: 'Created the event', rename_event: 'Renamed the event', add_lines: 'Added parts',
  import_lines: 'Imported a part list', remove_lines: 'Removed lines', exclude_lines: 'Left lines out',
  include_lines: 'Put lines back', keep_only_lines: 'Kept only some lines',
  set_quantity: 'Changed volumes', set_step: 'Changed buying steps',
  set_incumbent: 'Changed the current supplier', refresh_figures: 'Refreshed figures',
  set_terms: 'Changed terms', add_note: 'Added a note', edit_note: 'Edited a note',
  delete_note: 'Deleted a note', add_supplier: 'Added a supplier', add_suppliers: 'Added suppliers',
  remove_supplier: 'Removed a supplier',
  set_qualification: 'Changed a qualification', clear_qualification: 'Reset a qualification',
  cancel_event: 'Cancelled the event',
  // Phase 2 and 3 (Lane 1344, UI redesign slice 1: the Overview's activity reads these too).
  publish: 'Published the event', save_publish_draft: 'Saved the publish details',
  // Lane 1440: the RFP invitation letter, its marks and the launch date.
  save_invitation_letter: 'Edited the RFP invitation letter', set_supplier_invitation: 'Updated RFP invitations',
  set_launch_date: 'Changed the launch date',
  invite_supplier: 'Invited a supplier', invite_contact: 'Invited a contact',
  reissue_invitation: 'Sent a new invitation link', revoke_invitation: 'Withdrew an invitation',
  revoke_supplier: 'Removed a supplier from the event', set_line_supplier_note: 'Wrote a note for suppliers',
  compare_offers: 'Saved a bid summary', draft_feedback: 'Drafted feedback', close_round: 'Closed the round',
  open_round: 'Opened a new round', extend_due_date: 'Moved the due date', set_schedule: 'Changed the schedule', set_goal: 'Changed the goal', set_negotiation_target: 'Changed a negotiation target',
  dismiss_cleared_targets: 'Removed cleared negotiation targets', create_feedback: 'Drafted feedback',
  update_feedback: 'Edited feedback', discard_feedback: 'Discarded feedback', release_disclosure: 'Sent feedback',
  start_amendment: 'Started a change', publish_amendment: 'Published a change', discard_amendment: 'Discarded a change',
  add_document: 'Added a document', replace_document: 'Replaced a document', remove_document: 'Removed a document',
  set_document_flags: 'Changed who sees a document', set_document_audience: "Changed a document's name or who sees it",
  add_rule: 'Added an evaluation rule',
  edit_rule: 'Changed an evaluation rule', confirm_rule: 'Confirmed an evaluation rule',
  reject_rule: 'Deleted an evaluation rule', create_scenario: 'Made a scenario', edit_scenario: 'Changed a scenario',
  copy_scenario: 'Copied a scenario', archive_scenario: 'Archived a scenario',
  restore_scenario_revision: 'Restored a scenario', evaluate_scenario: 'Calculated a scenario',
  recommend: "Calculated Wingman's recommendation", compute_target: 'Worked out a target price',
  confirm_proposal: "Said yes to Wingman's proposal", use_current_rules: 'Moved a scenario to the evaluation rules',
  mark_preferred: 'Marked a scenario preferred', unmark_preferred: 'Unmarked a preferred scenario',
  add_standard_scenario: 'Made a standard scenario', create_reference_scenarios: 'Made the reference scenarios',
  review_note: 'Reviewed a supplier note',
};

function summarize(action, result) {
  if (!result) return '';
  const n = (k) => (typeof result[k] === 'number' ? result[k].toLocaleString() : null);
  if (action === 'create_event') return `${n('lines_added')} lines`;
  if (action === 'add_lines' || action === 'import_lines') return `${n('added')} added`;
  if (action === 'exclude_lines') return `${n('excluded')} lines`;
  if (action === 'include_lines') return `${n('included')} lines`;
  if (action === 'keep_only_lines') return `${n('kept')} kept, ${n('excluded')} left out`;
  if (action === 'remove_lines') return `${n('removed')} lines`;
  if (action === 'set_quantity' || action === 'set_step' || action === 'set_incumbent') return `${n('updated')} lines`;
  if (action === 'add_supplier') return result.supplier_name || '';
  if (action === 'add_suppliers') return (result.supplier_names || []).join(', ');
  if (action === 'rename_event') return result.name || '';
  return '';
}

export default function HistoryTab({ api, eventId, revision }) {
  const [items, setItems] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback((after) => {
    api.receipts(eventId, { limit: 50, cursor: after || undefined })
      .then((r) => { setItems((prev) => (after && prev ? [...prev, ...r.items] : r.items)); setCursor(r.next_cursor); })
      .catch((e) => setError(e.message));
  }, [api, eventId]);
  useEffect(() => { load(null); }, [load, revision]);
  if (error) return <div className="srce-error" role="alert">{error}</div>;
  if (!items) return <div className="ll-loading">Loading history…</div>;
  return (
    <div>
      <TableShell className="srce-tbl srce-tbl--hist" aria-label="Event history">
        <div className="srce-tr srce-th"><div>When</div><div>Who</div><div>What</div><div>Details</div><div>From</div></div>
        {items.map((r) => (
          <div key={r.receipt_id} className="srce-tr">
            <div className="srce-sub">{new Date(r.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
            <div>{r.user_name || 'A buyer'}</div>
            {/* Lane 1388: the server's words name what changed; a chat card the buyer
                applied counts as chat (from_chat). */}
            <div>{r.what || ACTION_TEXT[r.action] || r.action}</div>
            <div className="srce-sub">{r.what ? '' : summarize(r.action, r.result)}</div>
            <div className="srce-sub">{r.channel === 'chat' || r.from_chat ? 'Chat' : 'Screen'}</div>
          </div>
        ))}
      </TableShell>
      {cursor && <button type="button" className="srce-btn srce-gap" onClick={() => load(cursor)}>Show older</button>}
    </div>
  );
}
